import { useState } from 'react'

import { useDebouncedCallback } from 'use-debounce'

import { type SlackUser } from '@webapp/platform/integrations/entities/SlackUser'
import { useActiveUsersWithSlackModel } from '@webapp/platform/integrations/features/list-active-users-with-slack/models/useActiveUsersWithSlackModel'

const DEFAULT_PAGE_SIZE = 10
const SEARCH_DEBOUNCE_MS = 250

interface IUseActiveUsersWithSlackListPresenterProps {
  slackUserCache: Map<string, SlackUser>
  onUserLinkChange?: (userId: number, slackUser: SlackUser | null) => void
}

interface ModelParams {
  search: string
  page: number
  pageSize: number
}

export const useActiveUsersWithSlackListPresenter = ({
  slackUserCache,
  onUserLinkChange,
}: IUseActiveUsersWithSlackListPresenterProps) => {
  // Combine all API query params into a single state object so that updates
  // are always atomic (single re-render → single API request). This avoids
  // the React 17 non-batching issue where sequential setState calls in async
  // callbacks each trigger their own re-render, causing a spurious API request
  // with an inconsistent offset (e.g. new search term but stale page number).
  const [modelParams, setModelParams] = useState<ModelParams>({
    search: '',
    page: 1,
    pageSize: DEFAULT_PAGE_SIZE,
  })
  const [searchInputValue, setSearchInputValue] = useState('')

  const { search, page, pageSize } = modelParams
  const offset = (page - 1) * pageSize

  const updateDebouncedSearch = useDebouncedCallback((value: string) => {
    setModelParams((prev) => ({ ...prev, search: value, page: 1 }))
  }, SEARCH_DEBOUNCE_MS)

  const { users, pagination, isLoading, isFetching, isError, error } =
    useActiveUsersWithSlackModel({
      limit: pageSize,
      offset,
      search: search || undefined,
    })

  const handlePageChange = (newPage: number) => {
    setModelParams((prev) => ({ ...prev, page: newPage }))
  }

  const handlePageSizeChange = (newPageSize: number) => {
    setModelParams((prev) => ({ ...prev, pageSize: newPageSize, page: 1 }))
  }

  const handleSearchChange = (newSearch: string) => {
    setSearchInputValue(newSearch)
    updateDebouncedSearch(newSearch)
  }

  const handleSearchSubmit = (newSearch: string) => {
    updateDebouncedSearch.cancel()
    setSearchInputValue(newSearch)
    setModelParams((prev) => ({ ...prev, search: newSearch, page: 1 }))
  }

  return {
    data: {
      users,
      pagination,
      slackUserCache,
    },
    methods: {
      handlePageChange,
      handlePageSizeChange,
      handleSearchChange,
      handleSearchSubmit,
      // handleUserLinkChange is passed through from the Feature Set for cross-feature coordination
      // (e.g., cache invalidation after link/unlink operations by sibling features)
      handleUserLinkChange: onUserLinkChange,
    },
    states: {
      isLoading,
      isFetching,
      isError,
      error,
      page,
      pageSize,
      searchInputValue,
    },
  }
}
